import _ from "lodash";

import { ReportsProvider } from "./reports.provider";
import { failure, success, Result } from "../shared";
import {
  DrilldownData,
  ReportData,
  GetReportArgs,
  DrilldownsCsv,
} from "./reports.types";
import { CachedRequest, PromiseCache } from "../shared/promise.cache";

export enum ReportsServiceErrorCodes {
  GENERIC,
}

export class ReportsService {
  private readonly promiseCache = new PromiseCache<Result<ReportData>>();

  constructor(private reportsProvider: ReportsProvider) {}

  public async getDrilldowns(
    ...args: GetReportArgs
  ): Promise<Result<DrilldownData>> {
    try {
      const { data } = await this.reportsProvider.getDrilldowns(...args);
      const ordered = _.orderBy(data, "time");

      return success(ordered);
    } catch (e) {
      return failure(ReportsServiceErrorCodes.GENERIC);
    }
  }

  public async getDrilldownsCsv(
    endpoint: string
  ): Promise<Result<DrilldownsCsv>> {
    try {
      const response = await this.reportsProvider.getDrilldownsCsv(endpoint);
      const csv = {
        fileName:
          response.headers["content-disposition"].match(/filename=(.+)/)[1],
        content: response.data,
      };

      return success(csv);
    } catch (e) {
      return failure(ReportsServiceErrorCodes.GENERIC);
    }
  }

  public async get(...args: GetReportArgs): Promise<Result<ReportData>> {
    const requestId = args.join("-");

    if (this.promiseCache.has(requestId)) {
      return this.promiseCache.get(requestId);
    }

    const request: CachedRequest<Result<ReportData>> = async () => {
      try {
        const { data } = await this.reportsProvider.get(...args);
        const ordered = _.orderBy(data, "time");

        return success(ordered);
      } catch (e) {
        return failure(ReportsServiceErrorCodes.GENERIC);
      }
    };

    return this.promiseCache.set(requestId, request);
  }
}
